import { useState, useEffect } from "react";
import { Shield, BarChart3, Megaphone, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { type ConsentGrants } from "@/lib/consent-store";

interface CookiePreferencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialGrants?: ConsentGrants;
  onSave: (grants: ConsentGrants) => void;
}

interface CategoryRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  testId: string;
}

function CategoryRow({
  icon,
  title,
  description,
  checked,
  disabled,
  onCheckedChange,
  testId,
}: CategoryRowProps) {
  return (
    <div className="flex items-start gap-4 py-4 border-b border-gray-100 dark:border-[#1e3448] last:border-0">
      <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-primary/10 dark:bg-primary/15 flex items-center justify-center mt-0.5">
        <span className="text-primary">{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <span className="font-medium text-sm text-gray-900 dark:text-white">{title}</span>
          <Switch
            checked={checked}
            onCheckedChange={onCheckedChange}
            disabled={disabled}
            aria-label={`${title} cookies`}
            data-testid={testId}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}

export function CookiePreferencesModal({
  open,
  onOpenChange,
  initialGrants,
  onSave,
}: CookiePreferencesModalProps) {
  const [grants, setGrants] = useState<ConsentGrants>({
    analytics: initialGrants?.analytics ?? false,
    marketing: initialGrants?.marketing ?? false,
    personalization: initialGrants?.personalization ?? false,
  });

  // Re-sync internal state every time the modal opens or the stored grants
  // change so re-opening always shows the current choice, not stale defaults.
  useEffect(() => {
    if (open) {
      setGrants({
        analytics: initialGrants?.analytics ?? false,
        marketing: initialGrants?.marketing ?? false,
        personalization: initialGrants?.personalization ?? false,
      });
    }
  }, [open, initialGrants?.analytics, initialGrants?.marketing, initialGrants?.personalization]);

  function handleSave() {
    onSave(grants);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md w-full"
        data-testid="cookie-preferences-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Cookie preferences</DialogTitle>
          <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
            Choose which types of cookies you allow. Essential cookies are always on
            because they keep the site working.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-1">
          <CategoryRow
            icon={<Shield className="h-4 w-4" />}
            title="Essential"
            description="Required for the site to work: authentication, security, your session, and your consent choice itself. Cannot be disabled."
            checked={true}
            disabled={true}
            testId="toggle-essential-cookies"
          />
          <CategoryRow
            icon={<BarChart3 className="h-4 w-4" />}
            title="Analytics"
            description="Page-view counts and aggregate funnel reports that help us understand how visitors use the site and improve it."
            checked={grants.analytics}
            onCheckedChange={(v) => setGrants((g) => ({ ...g, analytics: v }))}
            testId="toggle-analytics-cookies"
          />
          <CategoryRow
            icon={<Megaphone className="h-4 w-4" />}
            title="Marketing / Advertising"
            description="Meta, LinkedIn, and Google Ads retargeting pixels that let us show you relevant ads on other platforms after your visit."
            checked={grants.marketing}
            onCheckedChange={(v) => setGrants((g) => ({ ...g, marketing: v }))}
            testId="toggle-marketing-cookies"
          />
          <CategoryRow
            icon={<Sparkles className="h-4 w-4" />}
            title="Personalization"
            description="Remembering content preferences and showing you information that is most relevant to your country and investor profile."
            checked={grants.personalization}
            onCheckedChange={(v) => setGrants((g) => ({ ...g, personalization: v }))}
            testId="toggle-personalization-cookies"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            onClick={handleSave}
            className="flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-semibold"
            data-testid="button-save-cookie-preferences"
          >
            Save preferences
          </Button>
          <Button
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="border-gray-300 dark:border-[#2a4560]"
            data-testid="button-cancel-cookie-preferences"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
